// Model Lab — one message through the whole pipeline, streamed.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, http, qs, stream } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  Confirm,
  Empty,
  Field,
  Search,
  Spinner,
  Tabs,
  useAsync,
  useToast,
} from "../ui";
import { dash, num, errMessage, ErrorBanner, Loadable } from "./format";
import {
  rec,
  str,
  pretty,
  sourceTypeLabel,
  sourceLabel,
  sourceBody,
  sourceRef,
  REPLY_ENGINES,
  brandReplyEngine,
  SAMPLES,
  type LabEvent,
  type LabSource,
  type LabFeedback,
  type LabRun,
  type LabRunsResponse,
  type FeedbackStatus,
} from "./lab";

export function ModelLabView() {
  const { can } = useSession();
  const canRun = can("settings.chatbot", "write") || can("chatbot.ai", "write");
  const toast = useToast();

  const [message, setMessage] = useState(SAMPLES[0]);
  const [sessionId, setSessionId] = useState("");
  const [replyEngine, setReplyEngine] = useState("gemma");
  const [running, setRunning] = useState(false);
  const [streamNotice, setStreamNotice] = useState("");
  const [events, setEvents] = useState<LabEvent[]>([]);
  const [current, setCurrent] = useState<LabRun | null>(null);
  const [selectedSource, setSelectedSource] = useState<LabSource | null>(null);
  const [tab, setTab] = useState("overview");
  const [query, setQuery] = useState("");
  const [note, setNote] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [historyNonce, setHistoryNonce] = useState(0);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const abort = useRef<(() => void) | null>(null);
  useEffect(() => () => abort.current?.(), []);

  const history = useAsync<LabRunsResponse>(
    () => http.get<LabRunsResponse>(`/chatbot/model-lab/runs${qs({ limit: 50 })}`),
    [historyNonce]
  );

  const loadRun = useCallback(async (id: string) => {
    setLoadError(null);
    try {
      const run = await http.get<LabRun>(`/chatbot/model-lab/runs/${encodeURIComponent(id)}`);
      setCurrent(run);
      setEvents(run.events ?? []);
      setNote(run.feedback?.note ?? "");
      setSelectedSource(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err : new ApiError(0, "client_error", errMessage(err, "load failed"), ""));
    }
  }, []);

  // ?message= prefills the composer, ?run= opens that run. There is no router
  // query hook in the shell, so the search string is read directly.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preset = params.get("message");
    if (preset) setMessage(preset);
    const run = params.get("run");
    if (run) void loadRun(run);
  }, [loadRun]);

  /** Fold one streamed frame into the run being assembled. */
  const applyFrame = useCallback((frame: LabEvent) => {
    setEvents((list) => [...list, frame]);
    setCurrent((prev) => {
      const next: LabRun = { ...(prev ?? {}) };
      const d = frame.data;
      switch (frame.type) {
        case "run_started":
          next.session_id = str(d.session_id);
          break;
        case "stage_completed": {
          const stage = str(d.stage);
          const kept = (next.stages ?? []).filter((s) => str(rec(s).stage) !== stage);
          next.stages = [...kept, d];
          break;
        }
        case "source_selected":
          next.sources = [...(next.sources ?? []), d as LabSource];
          break;
        case "model_usage":
          next.provider_usage = d;
          next.token_usage = rec(d.token_usage) as Record<string, number>;
          next.latency_ms = Number(d.latency_ms ?? next.latency_ms ?? 0);
          break;
        case "guardrail_result":
          next.guardrail_ok = Boolean(d.allowed);
          next.guardrail_details = d;
          next.response = { ...rec(next.response), guardrail_result: d };
          break;
        case "quality_signal":
          next.quality = d;
          next.confidence = Number(d.confidence ?? next.confidence ?? 0);
          next.graph_hit = Boolean(d.graph_hit);
          next.knowledge_hit = Boolean(d.knowledge_hit);
          break;
        case "command_preview":
          next.commands = Array.isArray(d.commands) ? (d.commands as Array<Record<string, unknown>>) : [];
          break;
        case "final_response":
          Object.assign(next, d as Partial<LabRun>);
          break;
        case "run_completed":
          next.run_id = str(d.run_id) || next.run_id;
          next.trace_id = str(d.trace_id) || next.trace_id;
          break;
        default:
          break;
      }
      return next;
    });
    if (frame.type === "run_failed") setStreamNotice(str(frame.data.error) || "Run failed");
  }, []);

  const stop = () => {
    abort.current?.();
    abort.current = null;
    setRunning(false);
    setStreamNotice("Stopped by the operator — the frames above are everything the run had emitted.");
  };

  const runLab = () => {
    if (!message.trim()) {
      toast.push("Enter a message to test", "critical");
      return;
    }
    const engine = REPLY_ENGINES[replyEngine] ?? REPLY_ENGINES.gemma;
    const body = {
      message,
      session_id: sessionId || undefined,
      message_type: "text",
      provider: engine.provider,
      model: engine.model,
      metadata: { source: "model_lab_ui" },
    };

    abort.current?.();
    setRunning(true);
    setStreamNotice("");
    setEvents([]);
    setSelectedSource(null);
    setCurrent({
      assistant_response: "",
      stages: [],
      sources: [],
      commands: [],
      token_usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
      spans: [],
      events: [],
    });

    abort.current = stream(
      "/chatbot/model-lab/run/stream",
      body,
      (event, raw) => {
        try {
          const parsed = JSON.parse(raw) as { type?: string; data?: unknown };
          applyFrame({ type: parsed.type ?? event, data: rec(parsed.data ?? parsed) });
        } catch {
          // A frame we cannot read is not worth failing the run for.
        }
      },
      (err) => {
        abort.current = null;
        // An operator abort resolves with no error, so it must not trigger the
        // fallback — re-running the whole pipeline is the opposite of stopping.
        if (!err) {
          setRunning(false);
          setHistoryNonce((n) => n + 1);
          return;
        }
        // The stream is best effort. When it cannot be read at all, the run is
        // re-driven through the non-streaming endpoint so the operator still
        // gets the final trace — and the notice says the live view was lost.
        setStreamNotice("Live stream unavailable. Loading the final trace using the fallback run.");
        http
          .post<LabRun>("/chatbot/model-lab/run", {
            ...body,
            metadata: { source: "model_lab_ui", fallback_reason: errMessage(err, "stream_failed") },
          })
          .then((payload) => {
            setCurrent(payload);
            setEvents(payload.events ?? []);
            setHistoryNonce((n) => n + 1);
            toast.push("Model lab fallback run complete", "good");
          })
          .catch((fallbackErr: unknown) => {
            toast.push(errMessage(fallbackErr, "Failed to run model lab test"), "critical");
          })
          .finally(() => setRunning(false));
      }
    );
  };

  /** Returns false when nothing was written, so <Confirm> can stay open on failure. */
  const saveFeedback = async (status: FeedbackStatus): Promise<boolean> => {
    const runIdValue = current?.run_id;
    if (!runIdValue) {
      toast.push("Run must be saved before feedback can be added", "critical");
      return false;
    }
    setSavingFeedback(true);
    try {
      const saved = await http.post<LabFeedback>(
        `/chatbot/model-lab/runs/${encodeURIComponent(runIdValue)}/feedback`,
        { feedback_status: status, note, promote_to_eval: status === "promote_to_eval" }
      );
      setCurrent((prev) =>
        prev
          ? {
              ...prev,
              feedback: saved,
              feedback_status: saved.feedback_status ?? status,
              response: saved.eval_case_id
                ? { ...rec(prev.response), eval_case_id: saved.eval_case_id }
                : prev.response,
            }
          : prev
      );
      toast.push("Lab feedback saved", "good");
      setHistoryNonce((n) => n + 1);
      return true;
    } catch (err) {
      toast.push(errMessage(err, "Failed to save feedback"), "critical");
      return false;
    } finally {
      setSavingFeedback(false);
    }
  };

  const runs = history.data?.runs ?? [];
  const filteredHistory = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return runs;
    return runs.filter((run) =>
      [run.classification, run.message_preview, run.response_preview, run.trace_id, run.run_id].some((v) =>
        str(v).toLowerCase().includes(q)
      )
    );
  }, [runs, query]);

  // KG band / classified-by / emotion are populated by later telemetry stages;
  // every plausible location is read defensively and renders '-' until the
  // fields exist.
  const metrics: Array<[string, string]> = useMemo(() => {
    const run = current ?? {};
    const providerUsage = rec(run.provider_usage);
    const usage = rec(providerUsage.token_usage ?? run.token_usage);
    const provider = str(providerUsage.provider) || REPLY_ENGINES[replyEngine]?.provider || "google";
    const meta = { ...rec(rec(run.response).metadata), ...rec(run.metadata) };
    const retrieval = rec(run.retrieval);
    const kgBand = str(meta.kg_band) || str(rec(retrieval.verdict).band);
    const classifiedBy = str(meta.classified_by) || str(retrieval.classified_by);
    const emotion = str(meta.emotion) || str(rec(run.quality).emotion);
    return [
      ["Classification", str(run.classification) || "none"],
      ["Confidence", num(run.confidence).toFixed(2)],
      ["Latency ms", num(run.latency_ms).toFixed(1)],
      ["Detection", "Knowledge Graph"],
      ["KG band", kgBand || "-"],
      ["Classified by", classifiedBy || "-"],
      ["Emotion", emotion || "-"],
      ["Reply engine", brandReplyEngine(provider)],
      ["Tokens", num(usage.total_tokens).toFixed(0)],
      ["Graph hit", run.graph_hit ? "yes" : "no"],
      ["Knowledge hit", run.knowledge_hit ? "yes" : "no"],
      ["Guardrail", run.guardrail_ok === false ? "blocked" : "ok"],
      ["Feedback", str(run.feedback_status || run.feedback?.feedback_status) || "-"],
      ["Trace", str(run.trace_id) || "-"],
    ];
  }, [current, replyEngine]);

  const sources = current?.source_details ?? current?.sources ?? [];
  const traceEvents = current?.events ?? events;
  const retrieval = rec(current?.retrieval);

  const labTabs = [
    { id: "overview", label: "Overview" },
    { id: "trace", label: "Live Trace" },
    { id: "sources", label: `Sources (${sources.length})` },
    { id: "guardrails", label: "Guardrails" },
    { id: "commands", label: "Commands" },
    { id: "json", label: "JSON" },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Model Lab</h1>
          <div className="sub">
            Dry-run chatbot intelligence, sources, trace stages, guardrails and command previews. Nothing here sends a
            WhatsApp message.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => setHistoryNonce((n) => n + 1)} disabled={history.loading}>
            {history.loading && <span className="spinner" />}
            Refresh
          </button>
        </div>
      </div>

      <div className="lab">
        <div className="stack">
          <section className="card">
            <div className="card-head" style={{ padding: 0, border: 0, marginBottom: "var(--sp-3)" }}>
              <h3>Run test</h3>
            </div>
            {streamNotice && <Banner tone="warning">{streamNotice}</Banner>}
            {!canRun && (
              <Banner tone="info">
                You can read lab runs but not start one — a dry run needs chatbot write permission.
              </Banner>
            )}
            <Field label="Test user message">
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7} placeholder="Paste a test user message" />
            </Field>
            <Field label="Session ID" hint="Optional — reuse one to continue an existing dry-run thread.">
              <input value={sessionId} onChange={(e) => setSessionId(e.target.value)} placeholder="Session ID (optional)" />
            </Field>
            <div className="setting">
              <div className="text">
                <span className="muted" style={{ fontSize: 12 }}>
                  Detection engine
                </span>
              </div>
              {/* Detection is not a choice: the graph decides. Only the reply engine is selectable. */}
              <Badge tone="brand">Knowledge Graph</Badge>
            </div>
            <Field label="Reply model" hint={REPLY_ENGINES[replyEngine]?.hint}>
              <select value={replyEngine} onChange={(e) => setReplyEngine(e.target.value)}>
                {Object.entries(REPLY_ENGINES).map(([key, engine]) => (
                  <option key={key} value={key}>
                    {engine.label}
                  </option>
                ))}
              </select>
            </Field>
            <div className="row">
              <button className="btn primary block" onClick={runLab} disabled={running || !canRun}>
                {running && <span className="spinner" />}
                Run dry test
              </button>
              {running && (
                <button className="btn danger" onClick={stop}>
                  Stop
                </button>
              )}
            </div>
            <div className="col" style={{ marginTop: "var(--sp-3)" }}>
              {SAMPLES.map((sample) => (
                <button key={sample} className="btn sm" style={{ justifyContent: "flex-start" }} onClick={() => setMessage(sample)}>
                  <span className="truncate">{sample}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="card">
            <div className="card-head" style={{ padding: 0, border: 0, marginBottom: "var(--sp-3)" }}>
              <h3>History</h3>
              {history.loading && <Spinner label="Loading history" />}
            </div>
            <Search value={query} onChange={setQuery} placeholder="Filter runs" ariaLabel="Filter lab runs" />
            <Loadable loading={history.loading} error={history.error} what="Failed to load model lab history" cols={2}>
              {!filteredHistory.length ? (
                <Empty
                  title={runs.length ? "Nothing matches" : "No lab runs yet"}
                  body={runs.length ? "No run matches this filter." : "Run a dry test to record the first one."}
                />
              ) : (
                <div className="col" style={{ marginTop: "var(--sp-3)" }}>
                  {filteredHistory.map((run) => (
                    <button
                      key={run.run_id}
                      className="step-row"
                      onClick={() => run.run_id && void loadRun(run.run_id)}
                      aria-current={current?.run_id === run.run_id ? "true" : undefined}
                    >
                      <span className="grow" style={{ minWidth: 0 }}>
                        <span className="row">
                          <span className="truncate strong">{str(run.classification) || "unknown"}</span>
                          <span className="spacer" />
                          <Badge>{num(run.latency_ms).toFixed(0)}ms</Badge>
                        </span>
                        <span className="truncate muted" style={{ display: "block", fontSize: 12 }}>
                          {dash(run.message_preview)}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Loadable>
          </section>
        </div>

        <div className="stack">
          <ErrorBanner error={loadError} what="Failed to load the lab run" />
          {!current ? (
            <Empty
              title="Run a dry test"
              body="Preview model behaviour — classification, sources, guardrails and the command preview — without sending WhatsApp messages."
              icon="⚗"
            />
          ) : (
            <>
              <section className="card">
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  User test message
                </div>
                <p className="prewrap">{str(rec(current.request).message) || message}</p>
              </section>

              <section className="card">
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  Assistant / command preview
                </div>
                <p className="prewrap">
                  {str(current.assistant_response) ||
                    str(current.response_preview) ||
                    (running ? "Processing…" : "No response preview")}
                </p>
              </section>

              {current.run_id && (
                <section className="card">
                  <div className="row">
                    <div>
                      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                        Learning feedback
                      </div>
                      <div className="strong">
                        {str(current.feedback_status || current.feedback?.feedback_status) || "Not reviewed"}
                      </div>
                      {current.feedback?.eval_case_id && (
                        <div style={{ color: "var(--success)", fontSize: 12 }}>
                          Eval case: {current.feedback.eval_case_id}
                        </div>
                      )}
                    </div>
                    <span className="spacer" />
                    <Badge>Dry-run only</Badge>
                  </div>
                  <Field label="Reviewer note" hint="Optional. Stored with the verdict.">
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
                  </Field>
                  <div className="row">
                    <button className="btn" disabled={savingFeedback || !canRun} onClick={() => void saveFeedback("good")}>
                      Good
                    </button>
                    <button className="btn" disabled={savingFeedback || !canRun} onClick={() => void saveFeedback("bad")}>
                      Bad
                    </button>
                    <button
                      className="btn"
                      disabled={savingFeedback || !canRun}
                      onClick={() => void saveFeedback("needs_review")}
                    >
                      Review
                    </button>
                    {/* Promotion is append-only and one-way, so it is confirmed. */}
                    <button
                      className="btn primary"
                      disabled={savingFeedback || !canRun}
                      onClick={() => setPromoting(true)}
                    >
                      Promote to eval
                    </button>
                  </div>
                </section>
              )}

              <section className="card">
                <div className="row">
                  <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                    Live processing
                  </div>
                  <span className="spacer" />
                  <Badge>{events.length} events</Badge>
                  {running && <Spinner label="Run in flight" />}
                </div>
                {!events.length ? (
                  <p className="muted" style={{ fontSize: 12 }}>
                    Events will appear as the run progresses.
                  </p>
                ) : (
                  <div className="col">
                    {events.slice(-10).map((event, i) => (
                      <div key={`${event.type}-${i}`} className="think step">
                        <Badge
                          tone={
                            event.type.includes("completed")
                              ? "good"
                              : event.type.includes("failed")
                                ? "critical"
                                : "info"
                          }
                        >
                          {event.type}
                        </Badge>
                        <span className="truncate muted">
                          {str(
                            event.data.stage ?? event.data.title ?? event.data.classification ?? event.data.run_id
                          ) || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <div className="grid c3">
                {metrics.map(([label, value]) => (
                  <div key={label} className="stat">
                    <div className="label">{label}</div>
                    <div className="value truncate" style={{ fontSize: 15 }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="stack">
          <section className="card">
            <Tabs tabs={labTabs} active={tab} onChange={setTab} />

            {tab === "overview" && (
              <dl className="kv">
                {metrics.map(([label, value]) => (
                  <Fragment key={label}>
                    <dt>{label}</dt>
                    <dd className="truncate">{value}</dd>
                  </Fragment>
                ))}
              </dl>
            )}

            {tab === "trace" && (
              <div className="col">
                {(current?.spans ?? []).length > 0 && (
                  <>
                    <h4 className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                      OpenLLMetry-style spans
                    </h4>
                    {(current?.spans ?? []).map((span, i) => (
                      <div key={span.span_id ?? i} className="note">
                        <div className="who">
                          <span className="name">{dash(span.stage)}</span>
                          <span className="spacer" />
                          <Badge>{num(span.latency_ms).toFixed(1)}ms</Badge>
                        </div>
                        <div className="body muted" style={{ fontSize: 11 }}>
                          span {dash(span.span_id)} · parent {dash(span.parent_span_id)} · status {dash(span.status)} ·
                          trace {dash(span.trace_id)}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {!traceEvents.length ? (
                  <Empty title="No live trace events yet" body="Run a dry test, or open a saved run." />
                ) : (
                  traceEvents.map((event, i) => (
                    <div key={`${event.type}-${i}`} className="note">
                      <div className="who">
                        <span className="name">{event.type}</span>
                        <span className="spacer" />
                        {event.data.latency_ms !== undefined && (
                          <Badge>{num(event.data.latency_ms).toFixed(1)}ms</Badge>
                        )}
                      </div>
                      <pre className="pre-block" style={{ maxHeight: 130 }}>
                        {pretty(event.data)}
                      </pre>
                    </div>
                  ))
                )}
              </div>
            )}

            {tab === "sources" && (
              <div className="col">
                {!sources.length ? (
                  <Empty title="No sources selected" body="This turn was answered without retrieving anything." />
                ) : (
                  sources.map((source, i) => (
                    <button
                      key={`${str(source.source_type)}-${str(source.source_id)}-${i}`}
                      className="step-row"
                      onClick={() => setSelectedSource(source)}
                    >
                      <span className="grow" style={{ minWidth: 0 }}>
                        <span className="row">
                          <span className="truncate strong">{sourceLabel(source)}</span>
                          <span className="spacer" />
                          <Badge>{sourceTypeLabel(source.source_type)}</Badge>
                        </span>
                        {sourceBody(source) && (
                          <span className="truncate muted" style={{ display: "block", fontSize: 12 }}>
                            {sourceBody(source)}
                          </span>
                        )}
                        <span className="muted" style={{ display: "block", fontSize: 11 }}>
                          {Math.round(num(source.confidence) * 100)}% match
                          {source.freshness ? ` · ${source.freshness}` : ""}
                          {source.review_status ? ` · ${source.review_status}` : ""}
                          {source.user_facing_eligible ? " · shown to user" : ""}
                          {sourceRef(source) ? ` · ref ${sourceRef(source)}` : ""}
                        </span>
                      </span>
                    </button>
                  ))
                )}
                {selectedSource && (
                  <div className="note">
                    <div className="who">
                      <span className="name">{sourceLabel(selectedSource)}</span>
                      <span>{sourceTypeLabel(selectedSource.source_type)}</span>
                      <span className="spacer" />
                      <button className="btn sm ghost" onClick={() => setSelectedSource(null)}>
                        Close
                      </button>
                    </div>
                    <dl className="kv">
                      <dt>Source ID</dt>
                      <dd className="truncate">{dash(selectedSource.source_id)}</dd>
                      <dt>Node / chunk</dt>
                      <dd className="truncate">{dash(selectedSource.node_id ?? selectedSource.chunk_id)}</dd>
                      <dt>Why selected</dt>
                      <dd>{dash(selectedSource.why_selected)}</dd>
                      <dt>Provenance</dt>
                      <dd className="truncate">
                        {dash(
                          selectedSource.source_url ??
                            selectedSource.provenance?.url ??
                            selectedSource.provenance?.source_title
                        )}
                      </dd>
                      <dt>Excerpt</dt>
                      <dd>{dash(selectedSource.excerpt)}</dd>
                    </dl>
                  </div>
                )}
              </div>
            )}

            {tab === "guardrails" && (
              <div className="col">
                <div>
                  <div className="row">
                    <span className="strong">Output guardrail</span>
                    <span className="spacer" />
                    <Badge tone={current?.guardrail_ok === false ? "critical" : "good"}>
                      {current?.guardrail_ok === false ? "blocked" : "allowed"}
                    </Badge>
                  </div>
                  <pre className="pre-block">
                    {pretty(
                      current?.guardrail_details ??
                        rec(current?.response).guardrail_result ?? { allowed: current?.guardrail_ok !== false }
                    )}
                  </pre>
                </div>
                <div>
                  <div className="strong">Quality flags</div>
                  <pre className="pre-block">{pretty(current?.quality_flags ?? current?.quality ?? {})}</pre>
                </div>
                <div>
                  <div className="strong">Retrieval summary</div>
                  <pre className="pre-block" style={{ maxHeight: 220 }}>
                    {pretty({
                      backend: retrieval.backend,
                      fallback_reason: retrieval.fallback_reason,
                      chunks: Array.isArray(retrieval.chunks) ? retrieval.chunks.length : 0,
                      paths: Array.isArray(retrieval.paths) ? retrieval.paths.length : 0,
                    })}
                  </pre>
                </div>
              </div>
            )}

            {tab === "commands" && <pre className="pre-block">{pretty(current?.commands ?? [])}</pre>}

            {tab === "json" && (
              <pre className="pre-block" style={{ maxHeight: 620 }}>
                {pretty(current)}
              </pre>
            )}
          </section>
        </div>
      </div>

      <Confirm
        open={promoting}
        onClose={() => setPromoting(false)}
        onConfirm={async () => {
          // Confirm only closes when this resolves, and a failed promotion must
          // leave the operator looking at the dialog, not at a closed one.
          if (!(await saveFeedback("promote_to_eval"))) throw new Error("promotion failed");
        }}
        title="Promote this run to an eval case?"
        confirmLabel="Promote"
        body="Promotion is append-only and one-way: it writes a lab_eval_cases row and a quality_evals row. It does NOT create an eval result, so the case does not appear in a harness run until the harness itself picks it up."
      />
    </div>
  );
}
